import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import LanguageSwitcher from "./LanguageSwitcher";
import ThemeSwitcher from "./ThemeSwitcher";
import NotificationBell from "./NotificationBell";
import HeaderStatusBar from "./HeaderStatusBar";
import InstallPromptButton from "./InstallPromptButton";
import PushToggleButton from "./PushToggleButton";
import { LogOut, Trophy, User, Home, Shield, HelpCircle } from "lucide-react";

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const { t } = useI18n();
    const nav = useNavigate();
    const loc = useLocation();
    const isLanding = loc.pathname === "/";

    return (
        <div className="grain min-h-screen relative" data-testid="layout">
            <header className="sticky top-0 z-30 [background-color:color-mix(in_srgb,var(--bg-base)_80%,transparent)] backdrop-blur-xl backdrop-saturate-150 border-b border-[var(--border-base)]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
                    <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2 group" data-testid="logo-link">
                        <div className="h-7 w-7 rounded-md bg-[var(--accent-hex)] flex items-center justify-center flex-shrink-0">
                            <Trophy className="h-4 w-4 text-[var(--accent-text)]" />
                        </div>
                        <span className="font-heading text-base sm:text-lg font-extrabold tracking-tight uppercase text-[var(--text-primary)] leading-none">
                            <span className="hidden sm:inline">FIFA World Cup </span>
                            <span className="sm:hidden">FIFA </span>
                            <span className="text-[var(--accent-hex)]">2026</span>
                        </span>
                    </Link>

                    <nav className="flex items-center gap-1 sm:gap-2">
                        <LanguageSwitcher />
                        <ThemeSwitcher />
                        {user && (
                            <>
                                <PushToggleButton />
                                <NotificationBell />
                                <Link
                                    to="/dashboard"
                                    data-testid="nav-dashboard"
                                    className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[var(--text-secondary)] hover:text-[var(--text-primary)] px-3 py-2 transition-colors"
                                >
                                    <Home className="h-3.5 w-3.5" /> {t("nav.dashboard")}
                                </Link>
                                <Link
                                    to="/help"
                                    data-testid="nav-help"
                                    title={t("nav.help")}
                                    className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-[var(--text-secondary)] hover:text-[var(--text-primary)] px-2 py-2 transition-colors"
                                >
                                    <HelpCircle className="h-3.5 w-3.5" />
                                    <span className="hidden sm:inline">{t("nav.help")}</span>
                                </Link>
                                {user.role === "admin" && (
                                    <Link
                                        to="/admin"
                                        data-testid="nav-global-admin"
                                        className="hidden sm:inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-[var(--accent-hex)] hover:opacity-80 px-2 py-2 transition-colors"
                                        title="Global Admin"
                                    >
                                        <Shield className="h-3.5 w-3.5" />
                                    </Link>
                                )}
                                <Link
                                    to="/profile"
                                    data-testid="nav-profile"
                                    title={t("nav.profile")}
                                    className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md border border-[var(--border-base)] bg-[var(--bg-card)] hover:border-[var(--accent-border)] transition-colors"
                                >
                                    <User className="h-3.5 w-3.5 text-[var(--accent-hex)]" />
                                    <span className="text-xs font-bold text-[var(--text-primary)]" data-testid="user-name">{user.name}</span>
                                    {user.role === "admin" && (
                                        <span className="text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded bg-[var(--accent-hex)] text-[var(--accent-text)]">
                                            {t("nav.admin_badge")}
                                        </span>
                                    )}
                                </Link>
                                <button
                                    onClick={async () => { await logout(); nav("/"); }}
                                    data-testid="logout-btn"
                                    className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-[var(--text-secondary)] hover:text-[var(--danger)] px-2 py-2 transition-colors"
                                >
                                    <LogOut className="h-3.5 w-3.5" />
                                </button>
                                <InstallPromptButton />
                            </>
                        )}
                        {!user && !isLanding && (
                            <>
                                <Link
                                    to="/help"
                                    data-testid="nav-help-guest"
                                    title={t("nav.help")}
                                    className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-[var(--text-secondary)] hover:text-[var(--text-primary)] px-2 py-2 transition-colors"
                                >
                                    <HelpCircle className="h-3.5 w-3.5" />
                                    <span className="hidden sm:inline">{t("nav.help")}</span>
                                </Link>
                                <Link
                                    to="/login"
                                    data-testid="nav-login"
                                    className="text-xs uppercase tracking-widest font-bold bg-[var(--accent-hex)] text-[var(--accent-text)] px-4 py-2 rounded-md hover:bg-[var(--accent-hex-hover)] transition-colors"
                                >
                                    {t("nav.signin")}
                                </Link>
                            </>
                        )}
                    </nav>
                </div>
                {user && <HeaderStatusBar />}
            </header>

            <main className="relative z-10">{children}</main>

            <footer className="mt-16 border-t border-[var(--border-base)] py-6 text-center text-xs text-[var(--text-muted)]" data-testid="footer">
                <span className="font-heading uppercase tracking-widest">FIFA World Cup 2026</span> · {t("layout.footer")}
            </footer>
        </div>
    );
}
